import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "./useArticleAnalytics";

/* Dwell-sampled reading progress (stabilized max paragraph depth) + resume-position restore. */
export const useReadingProgress = (article?: { id: string; slug: string; locked?: boolean } | null) => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const wantsResume = params.get("resume") === "1";
  const enabled = !!user && !!article && !article.locked;
  const aid = enabled ? article!.id : undefined;
  const slug = enabled ? article!.slug : undefined;

  useEffect(() => {
    if (!aid || !slug) return;
    let maxIdx = -1, total = 0, lastSent = -10;
    const sample = () => {
      if (document.visibilityState !== "visible") return;
      const ps = document.querySelectorAll<HTMLElement>(".article-body p");
      total = ps.length;
      if (!total) return;
      const line = window.innerHeight * 0.85;
      for (let i = ps.length - 1; i >= 0; i--) {
        if (ps[i].getBoundingClientRect().top < line) {
          if (i > maxIdx) maxIdx = i;
          break;
        }
      }
    };
    const persist = () => {
      if (maxIdx < 0 || !total) return;
      const p = Math.min(100, ((maxIdx + 1) / total) * 100);
      if (p - lastSent < 3) return;
      lastSent = p;
      api.post("/account/reading-progress", {
        article_id: aid, progress_percent: Math.round(p * 10) / 10,
        anchor_index: maxIdx, anchor_total: total,
      }).catch(() => {});
    };
    const sampler = setInterval(sample, 2000);
    const saver = setInterval(persist, 20000);
    const onVis = () => { if (document.visibilityState === "hidden") persist(); };
    document.addEventListener("visibilitychange", onVis);
    const warm = setTimeout(sample, 2500);
    return () => {
      clearInterval(sampler); clearInterval(saver); clearTimeout(warm);
      document.removeEventListener("visibilitychange", onVis);
      persist();
    };
  }, [aid, slug]);

  useEffect(() => {
    if (!enabled || !wantsResume || !slug) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/account/reading-position/${slug}`);
        if (cancelled || !data) return;
        const { anchor_index, anchor_total, progress_percent } = data;
        if (anchor_index == null || !anchor_total || progress_percent >= 90) return;
        const ps = document.querySelectorAll<HTMLElement>(".article-body p");
        if (ps.length !== anchor_total || anchor_index >= ps.length) return;
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        ps[anchor_index].scrollIntoView({ block: "start", behavior: reduce ? "auto" : "smooth" });
        trackEvent("resume_position_restored", { slug, anchor_index });
      } catch { /* fall back to top of article */ }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [enabled, wantsResume, slug]);
};
